import React, { useState } from 'react';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaQrcode, FaShareAlt } from 'react-icons/fa';
import './ShareToSocialMedia.css';

const shareChannels = [
  { id: 'facebook', label: 'Facebook', icon: <FaFacebookF aria-hidden="true" /> },
  { id: 'x', label: 'X', icon: <span className="share-button-x-icon" aria-hidden="true">X</span> },
  { id: 'linkedin', label: 'LinkedIn', icon: <FaLinkedinIn aria-hidden="true" /> },
  { id: 'instagram', label: 'Instagram', icon: <FaInstagram aria-hidden="true" /> },
  { id: 'native', label: 'Share', icon: <FaShareAlt aria-hidden="true" /> },
];

const getCurrentUrl = () => (
  typeof window !== 'undefined' ? window.location.href : ''
);

const canUseNativeShare = () => (
  typeof navigator !== 'undefined' && typeof navigator.share === 'function'
);

const canUseClipboard = () => (
  typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function'
);

const getShareUrl = (channelId, url, text) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);

  if (channelId === 'facebook') {
    return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  }

  if (channelId === 'x') {
    return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
  }

  if (channelId === 'linkedin') {
    return `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedText}`;
  }

  return '';
};

const ShareToSocialMedia = ({
  url,
  message,
  profileName = 'MATRIX profile',
  profileHandle = '',
  qrCodeUrl = '',
  title = 'Share Profile',
}) => {
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [shareComment, setShareComment] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const resolvedUrl = url || getCurrentUrl();
  const resolvedMessage = message || `Check out ${profileName} on MATRIX.`;
  const shareText = [shareComment.trim(), resolvedMessage].filter(Boolean).join('\n\n');

  const closeModal = () => {
    setSelectedChannel(null);
    setShareComment('');
    setStatusMessage('');
  };

  const openShareModal = (channel) => {
    setSelectedChannel(channel);
    setStatusMessage('');
  };

  const copyShareText = async () => {
    if (!canUseClipboard()) {
      return false;
    }

    await navigator.clipboard.writeText(`${shareText}\n${resolvedUrl}`.trim());
    return true;
  };

  const handleConfirmShare = async () => {
    if (!selectedChannel) {
      return;
    }

    if (selectedChannel.id === 'native' || selectedChannel.id === 'instagram' || selectedChannel.id === 'qr') {
      if (canUseNativeShare()) {
        try {
          await navigator.share({ title, text: shareText, url: resolvedUrl });
          closeModal();
          return;
        } catch (error) {
          if (error?.name === 'AbortError') {
            return;
          }
        }
      }

      const didCopy = await copyShareText();
      setStatusMessage(didCopy ? 'Share text copied.' : 'Copy this profile link from the preview below.');
      return;
    }

    const shareUrl = getShareUrl(selectedChannel.id, resolvedUrl, shareText);

    if (shareUrl && typeof window !== 'undefined') {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
      closeModal();
    }
  };

  return (
    <>
      <div className="share-buttons" aria-label={title}>
        {shareChannels.map((channel) => (
          <button
            key={channel.id}
            type="button"
            className={`share-button ${channel.id}`}
            onClick={() => openShareModal(channel)}
            aria-label={`Share to ${channel.label}`}
          >
            {channel.icon}
          </button>
        ))}
        {qrCodeUrl && (
          <button
            type="button"
            className="share-button qr"
            onClick={() => openShareModal({ id: 'qr', label: 'QR Share', icon: <FaQrcode aria-hidden="true" /> })}
            aria-label="Share QR card"
          >
            <FaQrcode aria-hidden="true" />
          </button>
        )}
      </div>

      {selectedChannel && (
        <div className="profile-share-modal-overlay" onClick={closeModal}>
          <div
            className="profile-share-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${title} modal`}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="profile-share-modal-header">
              <div>
                <p>{selectedChannel.label}</p>
                <h3>{title}</h3>
              </div>
              <button type="button" className="profile-share-modal-close" onClick={closeModal} aria-label="Close share modal">
                X
              </button>
            </header>

            <label className="profile-share-modal-field">
              <span>Add a comment</span>
              <textarea
                value={shareComment}
                onChange={(event) => setShareComment(event.target.value)}
                placeholder="Add a note before sharing..."
                rows={4}
              />
            </label>

            <article className="profile-share-preview-card" aria-label="Profile share preview">
              <div>
                <p className="profile-share-preview-eyebrow">MATRIX</p>
                <h4>{profileName}</h4>
                {profileHandle && <p className="profile-share-preview-handle">@{String(profileHandle).replace(/^@/, '')}</p>}
                <p className="profile-share-preview-copy">{shareComment.trim() || resolvedMessage}</p>
                <a href={resolvedUrl} target="_blank" rel="noreferrer">{resolvedUrl}</a>
              </div>
              {qrCodeUrl && (
                <img src={qrCodeUrl} alt={`${profileName} QR code`} className="profile-share-preview-qr" />
              )}
            </article>

            {statusMessage && <p className="profile-share-modal-status">{statusMessage}</p>}

            <div className="profile-share-modal-actions">
              <button type="button" className="profile-share-modal-cancel" onClick={closeModal}>Cancel</button>
              <button type="button" className="profile-share-modal-submit" onClick={handleConfirmShare}>
                {selectedChannel.id === 'instagram' || selectedChannel.id === 'native' || selectedChannel.id === 'qr' ? 'Share or Copy' : `Share to ${selectedChannel.label}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareToSocialMedia;

